import { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ListVideo,
  Briefcase,
  Network,
  GraduationCap,
  Orbit,
  Store,
  BookOpen,
  Home,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Check, Blocks, Landmark, MonitorPlay, LibraryBig,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { useDirection } from "../hooks/useDirection";
import ZoneSidebarContext from "../context/ZoneSidebarContext";
import { useSpaces } from "../context/SpaceProvider";
import { getSpaceIcon } from "./spaces/utils/spaceIconMap";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "./ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

interface ZoneInfo {
  icon: LucideIcon;
  label: string;
}

const ZoneSidebar = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const ctx = useContext(ZoneSidebarContext);
  const { spaces } = useSpaces();

  const collapsed = ctx?.collapsed ?? false;
  const toggleCollapsed = ctx?.toggleCollapsed ?? (() => {});
  const setPortalContainer = ctx?.setPortalContainer;

  const [portalActiveTab, setPortalActiveTab] = useState(
    () => localStorage.getItem("portal_active_tab") || "activities",
  );

  // Track the locally selected space from SpacesView
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      setPortalActiveTab((e as CustomEvent).detail);
    };
    window.addEventListener("portal-tab-change", handler);
    return () => window.removeEventListener("portal-tab-change", handler);
  }, []);

  useEffect(() => {
    if (pathname === "/portal") {
      setPortalActiveTab(
        localStorage.getItem("portal_active_tab") || "activities",
      );
    }
  }, [pathname]);

  // Listen for selected space changes from SpacesView
  useEffect(() => {
    const handler = (e: Event) => {
      setSelectedSpaceId((e as CustomEvent).detail);
    };
    window.addEventListener("spaces-selected-change", handler);
    return () => window.removeEventListener("spaces-selected-change", handler);
  }, []);

  // Clear selected space when navigating away from /spaces
  useEffect(() => {
    if (pathname !== "/spaces") {
      setSelectedSpaceId(null);
    }
  }, [pathname]);

  const selectedSpace = spaces.find((s) => s._id === selectedSpaceId) || null;

  const getZoneInfo = (): ZoneInfo | null => {
    if (pathname === "/home") return { icon: Home, label: t("home") };
    if (pathname.startsWith("/mentoring"))
      return { icon: GraduationCap, label: t("mentoring") || "Mentoring" };
    if (pathname === "/portal") {
      const tabMap: Record<string, ZoneInfo> = {
        activities: { icon: MonitorPlay, label: t("activities") },
        my_workspace: { icon: Blocks, label: t("workspace") },
        my_groups: { icon: Network, label: t("groups") },
      };
      return tabMap[portalActiveTab] || tabMap.activities;
    }
    if (pathname === "/spaces") {
      if (selectedSpace) {
        return { icon: getSpaceIcon(selectedSpace.icon), label: selectedSpace.name };
      }
      return { icon: Orbit, label: t("spaces") };
    }
    if (pathname.startsWith("/marketplace"))
      return { icon: LibraryBig, label: t("marketplace") };
    if (pathname === "/examples")
      return { icon: BookOpen, label: t("examples") };
    if (pathname === "/account")
      return { icon: Home, label: t("my_account") };
    if (pathname === "/profile")
      return { icon: Home, label: t("profile") };
    return null;
  };

  const zone = getZoneInfo();

  if (!zone) return null;

  const Icon = zone.icon;
  const tooltipSide = isRTL ? ("left" as const) : ("right" as const);
  const isSpacesZone = pathname === "/spaces" && !!selectedSpace?.color;
  const spaceColor = selectedSpace?.color;
  const isMarketplace = pathname.startsWith("/marketplace");

  return (
    <TooltipProvider>
      <div
        className={cn(
          "relative group/sidebar shrink-0 border-e bg-background flex flex-col transition-[width] duration-200 ease-linear",
          collapsed ? "w-[52px]" : "w-52",
          isMarketplace ? "border-primary/10" : "border-gray-200/60",
        )}
        style={isMarketplace ? {
          background: "linear-gradient(180deg, hsl(221 80% 61% / 0.10) 0%, hsl(260 60% 58% / 0.08) 35%, hsl(330 65% 60% / 0.06) 70%, hsl(260 50% 68% / 0.03) 100%)",
        } : undefined}
      >
        {/* Toggle button — absolute on the border, visible on hover */}
        <button
          onClick={toggleCollapsed}
          className={cn(
            "absolute top-12 z-10 w-6 h-6 rounded-full border border-gray-200 bg-white shadow-sm flex items-center justify-center",
            "text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all",
            "opacity-0 group-hover/sidebar:opacity-100",
            isRTL ? "-left-3" : "-right-3",
          )}
        >
          {collapsed
            ? isRTL
              ? <ChevronsLeft className="w-3 h-3" />
              : <ChevronsRight className="w-3 h-3" />
            : isRTL
              ? <ChevronsRight className="w-3 h-3" />
              : <ChevronsLeft className="w-3 h-3" />}
        </button>

        {/* Zone title — dropdown picker when a space is selected */}
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center pt-6 pb-5">
                <div
                  className={cn("w-8 h-8 rounded-lg flex items-center justify-center", !isSpacesZone && "bg-primary/10")}
                  style={isSpacesZone ? { backgroundColor: `${spaceColor}15` } : undefined}
                >
                  <Icon
                    className={cn("w-4 h-4", !isSpacesZone && "text-primary")}
                    style={isSpacesZone ? { color: spaceColor } : undefined}
                  />
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>{zone.label}</TooltipContent>
          </Tooltip>
        ) : isSpacesZone && selectedSpace ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 px-5 pt-6 pb-5 w-full hover:bg-muted/30 transition-colors group text-start">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${spaceColor}15` }}
                >
                  <Icon className="w-4 h-4" style={{ color: spaceColor }} />
                </div>
                <h2 className="text-[15px] font-semibold text-foreground tracking-tight truncate flex-1">
                  {zone.label}
                </h2>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              {spaces.map((space) => {
                const SpaceIcon = getSpaceIcon(space.icon);
                return (
                  <DropdownMenuItem
                    key={space._id}
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent("spaces-select-space", { detail: space._id }),
                      );
                    }}
                    className="flex items-center gap-3 py-2.5 cursor-pointer"
                  >
                    <SpaceIcon className="w-4 h-4 shrink-0" style={{ color: space.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{space.name}</div>
                      {space.identityLabel && (
                        <div className="text-xs text-gray-400 truncate">{space.identityLabel}</div>
                      )}
                    </div>
                    {space._id === selectedSpace._id && (
                      <Check className="w-4 h-4 text-primary shrink-0" />
                    )}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("spaces-select-space", { detail: null }));
                }}
                className="flex items-center gap-3 py-2.5 cursor-pointer"
              >
                <Orbit className="w-4 h-4 shrink-0 text-indigo-500" />
                <span className="text-sm font-medium">{t("all_spaces", "All spaces")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex items-center gap-2.5 px-5 pt-6 pb-5">
            <div
              className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", !isSpacesZone && "bg-primary/10")}
              style={isSpacesZone ? { backgroundColor: `${spaceColor}15` } : undefined}
            >
              <Icon
                className={cn("w-4 h-4", !isSpacesZone && "text-primary")}
                style={isSpacesZone ? { color: spaceColor } : undefined}
              />
            </div>
            <h2 className="text-[15px] font-semibold text-foreground tracking-tight truncate">
              {zone.label}
            </h2>
          </div>
        )}

        <div className={cn(
          "h-px",
          isMarketplace ? "bg-gradient-to-r from-transparent via-gray-200/60 to-transparent" : "bg-gray-200/60",
          collapsed ? "mx-2" : "mx-4",
        )} />

        {/* Portal target for zone-specific sidebar content */}
        <div
          ref={(el) => setPortalContainer?.(el)}
          className="flex-1 overflow-y-auto"
        />
      </div>
    </TooltipProvider>
  );
};

export default ZoneSidebar;
